import React from "react";
import { Award, TrendingUp, AlertTriangle, CheckCircle, Lightbulb } from "lucide-react";

// 主题变量化：等级色映射到语义变量，透明度用 color-mix 派生
const GRADE_STYLE = {
  S: {
    color: "var(--color-error)",
    bg: "color-mix(in srgb, var(--color-error) 10%, transparent)",
    bgSoft: "color-mix(in srgb, var(--color-error) 3%, transparent)",
    borderSoft: "color-mix(in srgb, var(--color-error) 13%, transparent)",
    label: "S · 强烈推荐",
  },
  A: {
    color: "var(--color-warning)",
    bg: "color-mix(in srgb, var(--color-warning) 10%, transparent)",
    bgSoft: "color-mix(in srgb, var(--color-warning) 3%, transparent)",
    borderSoft: "color-mix(in srgb, var(--color-warning) 13%, transparent)",
    label: "A · 推荐",
  },
  B: {
    color: "var(--color-blue-primary)",
    bg: "color-mix(in srgb, var(--color-blue-primary) 10%, transparent)",
    bgSoft: "color-mix(in srgb, var(--color-blue-primary) 3%, transparent)",
    borderSoft: "color-mix(in srgb, var(--color-blue-primary) 13%, transparent)",
    label: "B · 一般",
  },
  C: {
    color: "var(--color-text-muted)",
    bg: "color-mix(in srgb, var(--color-text-muted) 10%, transparent)",
    bgSoft: "color-mix(in srgb, var(--color-text-muted) 3%, transparent)",
    borderSoft: "color-mix(in srgb, var(--color-text-muted) 13%, transparent)",
    label: "C · 需大改",
  },
};

// 分数段配色：高=error 红、中=warning 橙、低=blue 蓝（主题变量）
const scoreColor = (score) =>
  score >= 8.5 ? "var(--color-error)" : score >= 7 ? "var(--color-warning)" : "var(--color-blue-primary)";

const ScoreBar = ({ score, maxScore = 10, color }) => (
  <div className="flex items-center gap-2 w-full">
    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${(score / maxScore) * 100}%`, backgroundColor: color }} />
    </div>
    <span className="text-xs font-bold w-8 text-right" style={{ color }}>{score}</span>
  </div>
);

const OutlineEvalResult = ({ data, theme }) => {
  if (!data) return null;

  const dimensions = data.dimensions || [];
  const grade = GRADE_STYLE[data.grade] || GRADE_STYLE.B;
  const ac = theme.colors.accentPrimary;

  return (
    <div className="space-y-4">
      {/* 评级卡片 */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Award size={18} style={{ color: ac }} />
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>大纲深度评估</span>
            </div>
            {data.gradeDescription && <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>{data.gradeDescription}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-2xl font-black" style={{ color: grade.color }}>{data.overallScore || "—"}</div>
              <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>/ 10</div>
            </div>
            <div className="px-3 py-1.5 rounded-lg text-sm font-bold" style={{ backgroundColor: grade.bg, color: grade.color }}>
              {grade.label}
            </div>
          </div>
        </div>

        {/* 五维雷达（简化为条形图） */}
        <div className="space-y-2.5">
          {dimensions.map((dim, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium" style={{ color: "var(--color-text-primary)" }}>{dim.name}</span>
              </div>
              <ScoreBar score={dim.score} maxScore={dim.maxScore || 10} color={scoreColor(dim.score)} />
            </div>
          ))}
        </div>
      </div>

      {/* 各维度详评 */}
      {dimensions.map((dim, i) => (
        <div key={i} className="rounded-xl p-4 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>{dim.name}</span>
            <span className="text-xs font-bold" style={{ color: scoreColor(dim.score) }}>{dim.score} 分</span>
          </div>
          {dim.comment && <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-secondary)" }}>{dim.comment}</p>}
          {dim.suggestions?.length > 0 && (
            <div className="space-y-1">
              {dim.suggestions.map((s, si) => (
                <div key={si} className="flex items-start gap-1.5 text-2xs" style={{ color: "var(--color-text-muted)" }}>
                  <Lightbulb size={10} className="mt-0.5 shrink-0" style={{ color: "var(--color-warning)" }} />
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* 优缺点 */}
      <div className="grid grid-cols-2 gap-3">
        {data.strengths?.length > 0 && (
          <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-1.5 mb-2"><CheckCircle size={13} style={{ color: "var(--color-success)" }} /><span className="text-xs font-bold" style={{ color: "var(--color-success)" }}>优点</span></div>
            <div className="space-y-1">{data.strengths.map((s, i) => <p key={i} className="text-2xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>• {s}</p>)}</div>
          </div>
        )}
        {data.weaknesses?.length > 0 && (
          <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-1.5 mb-2"><AlertTriangle size={13} style={{ color: "var(--color-warning)" }} /><span className="text-xs font-bold" style={{ color: "var(--color-warning)" }}>缺点</span></div>
            <div className="space-y-1">{data.weaknesses.map((s, i) => <p key={i} className="text-2xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>• {s}</p>)}</div>
          </div>
        )}
      </div>

      {/* 修改建议 */}
      {data.revisionSuggestions?.length > 0 && (
        <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center gap-1.5 mb-2"><Lightbulb size={13} style={{ color: ac }} /><span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>修改建议</span></div>
          <div className="space-y-1.5">{data.revisionSuggestions.map((s, i) => <div key={i} className="flex items-start gap-2 px-3 py-1.5 rounded-lg text-xs" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}><span className="font-bold shrink-0" style={{ color: ac }}>{i + 1}.</span>{s}</div>)}</div>
        </div>
      )}

      {/* 商业分析 */}
      {data.commercialAnalysis && (
        <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <div className="flex items-center gap-1.5 mb-2"><TrendingUp size={13} style={{ color: "var(--color-purple-primary)" }} /><span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>商业价值分析</span></div>
          <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.commercialAnalysis}</p>
        </div>
      )}

      {/* 总评 */}
      {data.summary && (
        <div className="rounded-xl p-4" style={{ backgroundColor: grade.bgSoft, border: `1px solid ${grade.borderSoft}` }}>
          <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{data.summary}</p>
        </div>
      )}
    </div>
  );
};

export default OutlineEvalResult;
